import { useNavigate } from 'react-router-dom';
import { ChangeEvent, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import useLoginContext from './useLoginContext';
import { createUser, loginUser, loginWithFirebase } from '../services/userService';
import { signInWithGithub, signInWithGoogle, auth } from '../services/firebaseService';

/**
 * Custom hook to manage authentication logic, including handling input changes,
 * form submission, password visibility toggling, error validation for both
 * login and signup processes, and session persistence for firebase login.
 *
 * @param authType - Specifies the authentication type ('login' or 'signup').
 * @returns {Object} An object containing:
 *   - username: The current value of the username input.
 *   - password: The current value of the password input.
 *   - passwordConfirmation: The current value of the password confirmation input (for signup).
 *   - showPassword: Boolean indicating whether the password is visible.
 *   - err: The current error message, if any.
 *   - handleInputChange: Function to handle changes in input fields.
 *   - handleSubmit: Function to handle form submission.
 *   - togglePasswordVisibility: Function to toggle password visibility.
 *   - handleOAuthSignIn: Function to handle Google and GitHub oauth.
 */
const useAuth = (authType: 'login' | 'signup') => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState<string>('');
  const { setUser } = useLoginContext();
  const navigate = useNavigate();

  /**
   * Check if there is a session that exists for firebase login.
   * Automatically sign in user if it does and redirect to home.
   */
  const checkSessionPersistence = async () => {
    auth.onAuthStateChanged(async (user: User | null) => {
      if (!user || !user.email) {
        setUser(null);
        return;
      }
      try {
        const authUser = await loginWithFirebase(user.uid, user.email);
        setUser(authUser);
        navigate('/home');
      } catch (error) {
        setUser(null);
      }
    });
  };

  /**
   * Toggles the visibility of the password input field.
   */
  const togglePasswordVisibility = () => {
    setShowPassword(prevState => !prevState);
  };

  /**
   * Handles changes in input fields and updates the corresponding state.
   *
   * @param e - The input change event.
   * @param field - The field being updated ('username', 'password', or 'confirmPassword').
   */
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    field: 'username' | 'password' | 'confirmPassword',
  ) => {
    const fieldText = e.target.value.trim();

    if (field === 'username') {
      setUsername(fieldText);
    } else if (field === 'password') {
      setPassword(fieldText);
    } else if (field === 'confirmPassword') {
      setPasswordConfirmation(fieldText);
    }
  };

  /**
   * Validates the input fields for the form.
   * Ensures required fields are filled and passwords match (for signup).
   *
   * @returns {boolean} True if inputs are valid, false otherwise.
   */
  const validateInputs = (): boolean => {
    if (username === '' || password === '') {
      setErr('Please enter a username and password');
      return false;
    }

    if (authType === 'signup' && password !== passwordConfirmation) {
      setErr('Passwords do not match');
      return false;
    }

    return true;
  };

  /**
   * Handles the submission of the form.
   * Validates input, performs login/signup, and navigates to the home page on success.
   *
   * @param event - The form submission event.
   */
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateInputs()) {
      return;
    }

    let user;

    try {
      if (authType === 'signup') {
        user = await createUser({ username, password });
      } else if (authType === 'login') {
        user = await loginUser({ username, password });
      } else {
        throw new Error('Invalid auth type');
      }

      setUser(user);
      navigate('/home');
    } catch (error) {
      setErr((error as Error).message);
    }
  };

  /**
   * Handles the Google oauth process.
   */
  const handleOAuthSignIn = async (type: 'google' | 'github') => {
    try {
      const { user } = type === 'google' ? await signInWithGoogle() : await signInWithGithub();
      if (!user?.email) throw new Error('Email required for oauth');
      const authUser = await loginWithFirebase(user.uid, user.email);
      setUser(authUser);
      navigate('/home');
    } catch (error) {
      setErr((error as Error).message);
    }
  };
  useEffect(() => {
    checkSessionPersistence();
  });
  return {
    username,
    password,
    passwordConfirmation,
    showPassword,
    err,
    handleInputChange,
    handleSubmit,
    togglePasswordVisibility,
    handleOAuthSignIn,
  };
};

export default useAuth;
